<template>
    <div>
        <EditKrpano
            v-if="canBeEditable"
            :krpanoHotspot="data.krpanoHotspot"
            :sceneKey="data.sceneKey"
            :type="data.type"
            :widgetTitle="data.widgetTitle"
            :editProperties="editProperties"
            :defaultProperties="defaultProperties"
            :loaded="widgetLoaded"
            :updateJsonUrl="updateJsonUrl"
            @set-camera="setCamera"
            type="krpano"
        />
        <MyHabiteoStudio
            v-if="isExportMediaEnabled && formUrl"
            :formUrl="formUrl"
            :zoomFactor="zoomFactor"
            :exportedBlobFile="exportedBlobFile"
            @export-media="exportMedia"
            @set-format="setImageFormat"
            @set-mention="setMention"
            @set-quality="setQuality"
            @set-preset="setPreset"
            @set-advanced_preset="setAdvancedPreset"
        />
        <LoaderProgressBar v-if="isLoadingScene" />
        <div
            v-show="!isLoadingScene"
            id="tour"
            ref="virtualTourHolder"
            class="content-widget virtual-tour"
            :class="{
                'edit-mode': canBeEditable,
                'plot-layout': data.type === 'plot',
                'standalone': standalone && !hasAlternateModel,
                'has-funnel': hasFunnel,
            }"
            :style="[
                (!data.items || data.activeHotspot && data.activeHotspot.type === 'virtual-tour' ) ? {
                    top:'60px', 
                    height: 'calc(100% - 60px)'
                } : null
            ]"
        />

        <FunnelFooter v-if="hasFunnel"/>

        <v-slide-x-transition>
            <v-btn
                v-show="hasBackButton"
                class="text-none previous-cta habiteo-body-2 py-4 ma-8"
                outlined
                :elevation="0"
                :ripple="false"
                color="green darken-1"
                @click.prevent="getBack"
            >
                {{ $t('utils.previous') }}
                <v-icon right>
                    icon icon-return
                </v-icon>
            </v-btn>
        </v-slide-x-transition>
    </div>
</template>


<script>
    import Http from '@/api/data';
    import { EventBus } from '@/event-bus';
    import { mapActions, mapGetters } from 'vuex';

    import EditKrpano from "@/components/editMode/EditKrpano";
    import MyHabiteoStudio from "@/components/myHabiteoStudio/MyHabiteoStudio";
    import LoaderProgressBar from '@/components/LoaderProgressBar';
    import FunnelFooter from "@/components/funnel/FunnelFooter";

    export default {
        name: 'VirtualTour',
        components: {
            EditKrpano,
            MyHabiteoStudio,
            LoaderProgressBar,
            FunnelFooter
        },
        props: [
            'data',
            'link',
            'active',
            'standalone'
        ],
        data: () => ({
            krpanoWidget: null,
            editProperties: {
                'viewAngle': null,
                'zoomFactor': null,
                'north': null
            },
            defaultProperties: {
                'viewAngle': 0,
                'zoomFactor': 0,
                'north': null
            },
            mouseTimeout: null,
            widgetLoaded: false,
            hotspotList:[],
            formUrl: null,
            exportedBlobFile: null,
            ignoredWidgetTypes: [
                'virtual-tour-models',
                'virtual-tour',
                'plot'
            ],
            isLoadingScene: false
        }),
        computed: {
            ...mapGetters([
                'isInternal',
                'isOfflineMode',
                'isReviewUse',
                'language',
                'protocol',
                'startedWidget',
                'isIframe',
                'isAlternativeMode'
            ]),
            ...mapGetters('widget', [
                'funnel'
            ]),
            isEditable() {
                return this.isInternal ? this.$route.query.edit !== undefined : false;
            },
            canBeEditable() {
                if (this.standalone && this.isEditable)
                    return true;
                return false;
            },
            isExportMediaEnabled() {
                return this.standalone && this.$route.query.mediaExport !== undefined;
            },
            updateJsonUrl() {
                return this.$props.data.private ? this.$props.data.private.krpanoURI : null;
            },
            isStudioEnabled() {
                return this.isExportMediaEnabled && this.formUrl ? true : false;
            },
            hasBackButton() {
                return this.startedWidget === 'drone-view';
            },
            zoomFactor() {
                return this.editProperties && this.editProperties.zoomFactor  ? this.editProperties.zoomFactor : 0;
            },
            apiUrl() {
                return this.protocol + (this.isReviewUse ? process.env.API_LIST.REVIEW : process.env.API_LIST.FO);
            },
            hasFunnel() {
                return this.funnel && !this.isFunnel;
            },
            isFunnel() {
                return this.isIframe && document.referrer === process.env.FUNNEL_URL;
            },
            hasAlternateModel() {
                return this.$props.data && this.$props.data.items ? ('submenu' in this.$props.data.items[0] && this.$props.data.items[0].submenu.length > 1) : false;
            }
        },
        async mounted() {
            this.render();
        },
        methods: {
            ...mapActions([
                'setIsLoading'
            ]),
            async render() {
                this.isLoadingScene = true;
                let urlLink = this.$props.link ? this.$props.link : this.apiUrl + '/request-data/virtual-tour' + window.location.search;
                if (this.language && !urlLink.includes('&language'))
                    urlLink += '&language=' + this.language;
                if (this.isOfflineMode && !urlLink.includes('&offline'))
                    urlLink += '&offline';
                await Http.getData(urlLink).then(payload => {
                    if (payload) {
                        payload.type = 'models';
                        payload.widgetType = 'models';
                        if (payload.gui)
                            EventBus.$emit('updateSidebar', Object.assign(this.$props.data, {'gui': payload.gui}), false);

                        if (payload.language)
                            this.$i18n.locale = payload.language;

                        let vm = this;
                        payload = Object.assign(payload, {
                            callbacks: {
                                sceneLoadedCallback (scene, newRoom) {
                                    // Reset default properties.
                                    vm.defaultProperties.viewAngle = [scene.camera[0], scene.camera[1]];
                                    vm.defaultProperties.zoomFactor = scene.camera[2];
                                    var data = Object.assign(vm.data, {
                                        'krpanoHotspot': scene.hotspots,
                                        'sceneKey': scene.id
                                    });
                                    EventBus.$emit('updateSidebar', data, false);
                                    vm.widgetLoaded = true;
                                },
                                viewChangeCallback(camera, index) {
                                    // Setup mouse handler.
                                    vm.mouseTimeout && clearTimeout(vm.mouseTimeout);
                                    vm.mouseTimeout = setTimeout(() => {
                                        vm.editProperties.viewAngle = [camera[0], camera[1]];
                                        vm.editProperties.zoomFactor = camera[2];
                                    }, 1000);
                                    vm.setIsLoading(false);
                                },
                                widgetReadyCallback(widget, data) {
                                    if (vm.$props.data.gui)
                                        data.gui = vm.$props.data.gui;

                                    EventBus.$emit('trackWidget', payload.tracking);
                                    vm.isLoadingScene = false;
                                },
                                exportMediaCallback(blob) {
                                    vm.exportedBlobFile = blob;
                                }
                            }
                        });

                        let jsonAPIUrl = this.isReviewUse ? payload.private.panoURI : payload.private.krpanoURI;
                        this.getKrpanoConfig(jsonAPIUrl, jsonData => {
                            payload.jsonConfig = jsonData;

                            if (this.isExportMediaEnabled && (payload.private && 'studioExport' in payload.private))
                                this.formUrl = payload.private.studioExport;

                            if (payload.gui && 'disclaimer' in payload.gui)
                                payload.disclaimer = payload.gui.disclaimer.text;

                            payload.isStudioEnabled = this.isStudioEnabled;

                            if (this.isReviewUse) {
                                jsonData['scenes'].forEach(scene => {
                                    if (scene.hotspots)
                                        scene.hotspots.sort((a, b) => a.name && b.name ? (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1) : a.name ? 1 : -1);
                                });
                                this.krpanoWidget = new Habiteo.PanoWidget(this.$refs.virtualTourHolder, jsonData, document.getElementById('fullScreen'));
                            } else {
                                Object.keys(jsonData).forEach(scene => {
                                    if (jsonData[scene].hotspots)
                                        jsonData[scene].hotspots.sort((a, b) => a.name && b.name ? (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1) : a.name ? 1 : -1);
                                });
                                this.krpanoWidget = new Habiteo.VirtualTourWidget(this.$refs.virtualTourHolder, payload, document.getElementById('fullScreen'));
                            }

                            if (this.standalone) {
                                EventBus.$emit('setSidebarData', payload);
                                EventBus.$emit('updateLoadedWidgets', {
                                    id: 'virtual-tour',
                                    link: urlLink,
                                    active: true,
                                    widgetType: 'virtual-tour'
                                });
                            }
                            else if (this.$props.data.widgetType && !this.ignoredWidgetTypes.includes(this.$props.data.widgetType))
                                EventBus.$emit('updateSidebar', payload, false);

                            EventBus.$emit('trackWidget', payload.tracking);

                            if (this.isReviewUse) {
                                this.isLoadingScene = false;
                            }
                        });
                    }
                }).catch(error => {
                    EventBus.$emit('setErrorState', true, error);
                });
            },
            getKrpanoConfig(apiUrl, callback) {
                Http.getData(apiUrl).then(payload => {
                    callback(payload);
                }).catch(error => {
                    console.error('Error - getKrpanoConfig');
                    callback(null);
                });
            },
            exportMedia(settings) {
                this.krpanoWidget.widget.shotPano(settings);
            },
            setImageFormat(format) {
                this.krpanoWidget.widget.exportFormat = format;
            },
            setPreset(preset) {
                this.krpanoWidget.widget.preset = preset;
            },
            setAdvancedPreset(advancedPreset){
                this.krpanoWidget.widget.preset = advancedPreset;
            },
            setMention(option) {
                this.krpanoWidget.widget.mention = option;
            },
            setQuality(quality) {
                this.krpanoWidget.widget.exportQuality = quality;
            },
            getBack() {
                EventBus.$emit('getBack');
            },
            setCamera(data) {
                if (data.metadata) {
                    let camera;
                    let currentScene = this.krpanoWidget.data.scenes.find(scene => scene.id === data.key);
                    if (data.metadata['viewAngle'] && data.metadata['zoomFactor'])
                        camera = data.metadata['viewAngle'].concat(data.metadata['zoomFactor']);
                    else if (data.metadata['viewAngle'])
                        camera = data.metadata['viewAngle'].concat([currentScene.camera[2]]);
                    else
                        camera = [currentScene.camera[0], currentScene.camera[1]].concat(data.metadata['zoomFactor']);
                    this.krpanoWidget.widget.setCamera(data.key, camera);
                }
            }
        },
        watch: {
            active(val) {
                if (val)
                    this.render();
                //TODO: loadData & unloadData ?
            },
            link(val, oldVal) {
                if (this.active && oldVal)
                    this.render();
            }
        }
    };
</script>

<style lang="scss" scoped>
    .previous-cta {
        background-color: white;
        position: absolute;
        border-radius: 8px;
        top: 0;
        left: 0;
    }

    .virtual-tour {
        .content-widget {
            &.has-funnel {
                height: calc(100% - 4%);
            }
        }
    }
</style>


// WEBPACK FOOTER //
// src/views/VirtualTourWidget.vue